import { useEffect, useMemo, useState } from "react";
import { getFontCoverage, type FontCoverage } from "helpers/fontGlyphCoverage";
import { usePublicMediaAssets, useLoadAllAssets } from "#src/hooks";
import { BrandKitAsset } from "pages/media/types";
import { resolveToBrandkitFontIfNeeded } from "../helpers/itemsHelper";
import { TemplateState } from "../types";
import { useTemplateEditorStore } from "../store/useTemplateEditorStore";
import { useTemplateData } from "./useTemplateData";

interface TextFontPair {
  text: string;
  fontUrl: string;
}

interface FontReference {
  fontPath?: string;
  fontAssetId?: string;
}

interface FontResolver {
  pathByAssetId: Map<string, string>;
  titleFont?: BrandKitAsset;
  subtitlesFont?: BrandKitAsset;
  textFont?: BrandKitAsset;
}

interface SectionText {
  topic?: string;
  script?: { content?: string };
}

interface PairAccumulator {
  pairs: TextFontPair[];
  titleFontUrls: Set<string>;
  subtitleFontUrls: Set<string>;
}

/**
 * Resolves a text item's font to a fetchable URL, the same way the renderer
 * does: an explicit `fontPath` wins; otherwise resolve `fontAssetId` (turning a
 * brand-kit placeholder into the real asset id) and look up that asset's path.
 */
const resolveFontUrl = (
  item: FontReference,
  resolver: FontResolver
): string | undefined => {
  if (item.fontPath) return item.fontPath;
  const assetId = resolveToBrandkitFontIfNeeded(
    item.fontAssetId,
    resolver.titleFont,
    resolver.subtitlesFont,
    resolver.textFont
  );
  return assetId ? resolver.pathByAssetId.get(assetId) : undefined;
};

const collectTemplatePairs = (
  template: TemplateState | undefined,
  resolver: FontResolver,
  collected: PairAccumulator
): void => {
  if (!template) return;

  const add = (content?: string, item?: FontReference) => {
    const text = content?.trim();
    if (!text || !item) return;
    const fontUrl = resolveFontUrl(item, resolver);
    if (fontUrl) collected.pairs.push({ text, fontUrl });
  };

  template.customTexts.forEach((item) => add(item.content, item));
  template.parameteredTexts?.forEach((item) => {
    if (item.visible !== false) add(item.content, item);
  });
  if (template.figure) {
    add(template.figure.number.content, template.figure.number);
    add(template.figure.units?.content, template.figure.units);
  }

  if (template.titleStyle.showTitle) {
    const url =
      resolveFontUrl(template.titleStyle, resolver) ?? resolver.titleFont?.path;
    if (url) collected.titleFontUrls.add(url);
  }
  if (template.textStyle.showSubtitles) {
    const url =
      resolveFontUrl(template.textStyle, resolver) ??
      resolver.subtitlesFont?.path ??
      resolver.textFont?.path;
    if (url) collected.subtitleFontUrls.add(url);
  }
};

const collectSectionPairs = (
  sections: readonly SectionText[] | undefined,
  collected: PairAccumulator
): void => {
  for (const section of sections ?? []) {
    const topic = section.topic?.trim();
    if (topic) {
      for (const fontUrl of collected.titleFontUrls) {
        collected.pairs.push({ text: topic, fontUrl });
      }
    }
    const script = section.script?.content?.trim();
    if (script) {
      for (const fontUrl of collected.subtitleFontUrls) {
        collected.pairs.push({ text: script, fontUrl });
      }
    }
  }
};

export interface FontGlyphWarningResult {
  hasUnsupported: boolean;
  /** Distinct names of the fonts that can't render some text. */
  fontNames: string[];
}

const sameStrings = (a: string[], b: string[]) =>
  a.length === b.length && a.every((value, i) => value === b[i]);

/** Scans every text element across all slides and reports the fonts that can't
 render their characters */
export const useFontGlyphWarning = (): FontGlyphWarningResult => {
  const templates = useTemplateEditorStore((state) => state.templates);
  const templateState = useTemplateEditorStore((state) => state.templateState);
  const { brandKitData, storyboardSections, assetLibrariesData } =
    useTemplateData();
  const { publicFonts } = usePublicMediaAssets();
  const orgAssetLibraryId =
    assetLibrariesData.organizationAssetLibraries[0]?.id;
  const { fonts: libraryFonts } = useLoadAllAssets({
    currentAssetLibraryId: orgAssetLibraryId ?? "",
  });

  const [fontNames, setFontNames] = useState<string[]>([]);

  const { titleFont, subtitlesFont, textFont } = brandKitData;

  const resolver = useMemo<FontResolver>(() => {
    const pathByAssetId = new Map<string, string>();
    const add = (id?: string | null, path?: string | null) => {
      if (id && path) pathByAssetId.set(id, path);
    };
    publicFonts?.forEach((font) => add(font.id, font.path));
    libraryFonts?.forEach((font) => add(font.id, font.path));
    [titleFont, subtitlesFont, textFont].forEach((font) =>
      add(font?.asset_id, font?.path)
    );
    return { pathByAssetId, titleFont, subtitlesFont, textFont };
  }, [publicFonts, libraryFonts, titleFont, subtitlesFont, textFont]);

  const pairs = useMemo<TextFontPair[]>(() => {
    const collected: PairAccumulator = {
      pairs: [],
      titleFontUrls: new Set(),
      subtitleFontUrls: new Set(),
    };
    templates.forEach((template) =>
      collectTemplatePairs(template, resolver, collected)
    );
    // The active slide's latest edits live in templateState, not yet in the Map.
    collectTemplatePairs(templateState, resolver, collected);
    collectSectionPairs(storyboardSections, collected);
    return collected.pairs;
  }, [templates, templateState, resolver, storyboardSections]);

  useEffect(() => {
    // Held in an object so a stale async task can be detected after `await`.
    const task = { cancelled: false };

    // Keep a stable reference when nothing changed so React can bail out.
    const commit = (names: string[]) =>
      setFontNames((prev) => (sameStrings(prev, names) ? prev : names));

    void (async () => {
      if (pairs.length === 0) {
        commit([]);
        return;
      }

      const uniqueUrls = [...new Set(pairs.map((pair) => pair.fontUrl))];
      const coverageByFontUrl = new Map<string, FontCoverage>();
      await Promise.all(
        uniqueUrls.map(async (url) => {
          coverageByFontUrl.set(url, await getFontCoverage(url));
        })
      );
      if (task.cancelled) return;

      const names = new Set<string>();
      for (const pair of pairs) {
        const coverage = coverageByFontUrl.get(pair.fontUrl);
        if ((coverage?.test(pair.text) ?? []).length > 0) {
          names.add(coverage?.fontName ?? pair.fontUrl);
        }
      }
      commit([...names]);
    })();

    return () => {
      task.cancelled = true;
    };
  }, [pairs]);

  return {
    hasUnsupported: fontNames.length > 0,
    fontNames,
  };
};
